import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveJob, removeSavedJob, getSavedJobs } from '../services/candidate/jobService';

export const useSavedJobs = (isLoggedIn) => {
    const navigate = useNavigate();
    const [savedJobsIds, setSavedJobsIds] = useState([]);
    const [savedJobs, setSavedJobs] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch saved jobs on mount or when login status changes
    useEffect(() => {
        const fetchSavedJobs = async () => {
            if (!isLoggedIn) {
                setSavedJobs([]);
                setSavedJobsIds([]);
                return;
            }

            setLoading(true);
            try {
                const response = await getSavedJobs();
                if (response.success) {
                    setSavedJobs(response.data);
                    setSavedJobsIds(response.data.map(job => job.jobData.id));
                }
            } catch (error) {
                console.error('Error fetching saved jobs:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSavedJobs();
    }, [isLoggedIn]);

    // Check if a specific job is saved
    const isJobSaved = useCallback((jobId) => {
        return savedJobsIds.includes(jobId);
    }, [savedJobsIds]);

    // Get saved job entry ID for removal
    const getSavedJobEntryId = useCallback((jobId) => {
        const savedJobEntry = savedJobs.find(saved => saved.jobData.id === jobId);
        return savedJobEntry ? savedJobEntry._id : null;
    }, [savedJobs]);

    // Toggle save status for a job
    const toggleSaveJob = useCallback(async (job, redirectPath = '/candidate/login') => {
        if (!isLoggedIn) {
            navigate(redirectPath);
            return false;
        }

        const isCurrentlySaved = savedJobsIds.includes(job.id);
        
        // Optimistic update
        setSavedJobsIds(prev => 
            isCurrentlySaved 
                ? prev.filter(id => id !== job.id)
                : [...prev, job.id]
        );

        try {
            if (isCurrentlySaved) {
                // Get the saved job entry to remove
                const savedJobId = getSavedJobEntryId(job.id);
                if (savedJobId) {
                    await removeSavedJob(savedJobId);
                    
                    // Also update the full savedJobs array
                    setSavedJobs(prev => prev.filter(item => item.jobData.id !== job.id));
                }
            } else {
                // Pass the entire job object to save
                const response = await saveJob(job);
                
                // If the API returns the saved job data, update the full savedJobs array
                if (response && response.data) {
                    setSavedJobs(prev => [...prev, response.data]);
                } else {
                    // If no data returned, we might want to refetch to keep in sync
                    // But for now, we'll just add the job ID we already have
                }
            }
            return true;
        } catch (error) {
            // Revert on error
            setSavedJobsIds(prev => 
                isCurrentlySaved 
                    ? [...prev, job.id]
                    : prev.filter(id => id !== job.id)
            );
            
            // Also revert the full savedJobs array if we were removing
            if (isCurrentlySaved) {
                // We need to add back the saved job entry, but we don't have it anymore
                // So we'll refetch to be safe
                const response = await getSavedJobs();
                if (response.success) {
                    setSavedJobs(response.data);
                }
            }
            
            console.error('Error toggling saved job:', error);
            return false;
        }
    }, [isLoggedIn, savedJobsIds, savedJobs, getSavedJobEntryId, navigate]);

    // Remove a saved job (alternative method if you have the saved job ID directly)
    const removeSavedJobById = useCallback(async (savedJobId, jobId) => {
        if (!isLoggedIn) {
            navigate('/candidate/login');
            return false;
        }

        // Optimistic update
        setSavedJobsIds(prev => prev.filter(id => id !== jobId));
        setSavedJobs(prev => prev.filter(item => item._id !== savedJobId));

        try {
            await removeSavedJob(savedJobId);
            return true;
        } catch (error) {
            // Revert on error - refetch to be safe
            console.error('Error removing saved job:', error);
            const response = await getSavedJobs();
            if (response.success) {
                setSavedJobs(response.data);
                setSavedJobsIds(response.data.map(job => job.jobData.id));
            }
            return false;
        }
    }, [isLoggedIn, navigate]);

    // Refetch saved jobs (useful after bulk operations)
    const refetchSavedJobs = useCallback(async () => {
        if (!isLoggedIn) return;
        
        try {
            const response = await getSavedJobs();
            if (response.success) {
                setSavedJobs(response.data);
                setSavedJobsIds(response.data.map(job => job.jobData.id));
            }
        } catch (error) {
            console.error('Error refetching saved jobs:', error);
        }
    }, [isLoggedIn]);

    return {
        savedJobsIds,
        savedJobs,
        loading,
        isJobSaved,
        toggleSaveJob,
        removeSavedJobById,
        refetchSavedJobs,
        getSavedJobEntryId
    };
};